/**
 * /programs — trainer Learning Programs library.
 *
 * Deliberately an exact structural mirror of CoursesListPage (product rule:
 * Course Library and Learning Programs share one design; only the data
 * differs): greeting header + create button, Tery search bar, three
 * single-select KPI status tiles with true totals, revalidation cue,
 * cross-fading card grid, dual empty states, pagination.
 */
import React, { useState, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { slideUp, EASE_OUT } from '@/lib/motion';
import PageHeader from '@/components/common/PageHeader';
import FeatureUpgradeNotice from '@/components/common/FeatureUpgradeNotice';
import { AcademicCapIcon } from '@heroicons/react/24/outline';
import { usePlanFeatures } from '@/hooks/usePlanFeatures';
import { usePrograms, useUpdateProgramStatus, useDeleteProgram } from '../../services/programs';
import type { LearningProgram } from '../../types/program';
import { ProgramStatus } from '../../types/program';
import { useDebounce } from '../../hooks/useDebounce';
import ProgramCard from '../../components/programs/ProgramCard';
import CourseIcon from '../../components/icons/CourseIcon';
import VerifiedIcon from '../../components/icons/VerifiedIcon';
import HiddenIcon from '../../components/icons/HiddenIcon';
import CourseLibraryIcon from '../../components/icons/CourseLibraryIcon';
import { PlusIcon } from '@heroicons/react/24/outline';
import terySearchImg from '../../../public/tery/tery_search.png';
import { phantomProps } from '@/config/phantom';
import { useTheme } from '@/contexts/ThemeContext';
import { KpiTile } from '@/components/shared/KpiTile';

const ITEMS_PER_PAGE = 12;
// The three filter tiles mirror the courses page. `disabled` programs have no
// tile (courses have no equivalent state) but stay visible in the unfiltered
// grid with their own status tag.
const TILE_STATUSES: ProgramStatus[] = ['draft', 'active', 'archived'];

const ProgramsListPage: React.FC = () => {
  const { programs: programsAllowed, plan } = usePlanFeatures();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const [searchParams, setSearchParams] = useSearchParams();

  const pageParam = parseInt(searchParams.get('page') || '1', 10);
  const searchParam = searchParams.get('search') || '';
  const selectedStatus = useMemo<ProgramStatus | null>(
    () => TILE_STATUSES.find((s) => searchParams.get('status') === s) ?? null,
    [searchParams]
  );

  const [searchInput, setSearchInput] = useState(searchParam);
  const reduceMotion = useReducedMotion();

  const debouncedSearch = useDebounce(searchInput, 300);

  React.useEffect(() => {
    const params = new URLSearchParams(searchParams);
    if (debouncedSearch) {
      params.set('search', debouncedSearch);
      params.set('page', '1');
    } else {
      params.delete('search');
    }
    setSearchParams(params, { replace: true });
  }, [debouncedSearch]);

  const queryParams = useMemo(
    () => ({
      page: pageParam,
      per_page: ITEMS_PER_PAGE,
      search: debouncedSearch || undefined,
      status: selectedStatus ?? undefined,
    }),
    [pageParam, debouncedSearch, selectedStatus]
  );

  const { data, isLoading, isFetching, error } = usePrograms(queryParams);
  const statusMutation = useUpdateProgramStatus();
  const deleteMutation = useDeleteProgram();

  const handleToggleHidden = async (program: LearningProgram) => {
    // 'disabled' is a legacy state nothing sets any more — historical rows
    // still read as hidden (Aleix, 2026-09-09).
    const hidden = program.status === 'archived' || program.status === 'disabled';
    try {
      await statusMutation.mutateAsync({
        programId: program.id,
        // Unhide returns to drafts — the trainer re-activates explicitly.
        status: hidden ? 'draft' : 'archived',
      });
    } catch {
      // surfaced by the mutation's toast
    }
  };

  const handleDelete = async (program: LearningProgram) => {
    if (window.confirm(`Delete "${program.name}"? This cannot be undone.`)) {
      try {
        await deleteMutation.mutateAsync(program.id);
      } catch {
        // surfaced by the mutation's toast
      }
    }
  };

  // Single-select: clicking the active tile clears the filter (show all);
  // clicking another replaces it. At most one `status` ever rides the URL.
  const selectStatus = (status: ProgramStatus) => {
    const params = new URLSearchParams(searchParams);
    params.delete('status');
    if (selectedStatus !== status) params.set('status', status);
    params.set('page', '1');
    setSearchParams(params);
  };

  const clearFilters = () => {
    setSearchInput('');
    const params = new URLSearchParams(searchParams);
    params.delete('status');
    params.delete('search');
    params.set('page', '1');
    setSearchParams(params);
  };

  const handlePageChange = (newPage: number) => {
    const params = new URLSearchParams(searchParams);
    params.set('page', newPage.toString());
    setSearchParams(params);
  };

  const handleCreate = () => {
    navigate('/programs/new');
  };

  const programs = Array.isArray(data?.programs) ? data!.programs : [];
  const { draft: draftCount, active: activeCount, archived: archivedCount } =
    data?.statusCounts ?? { draft: 0, active: 0, disabled: 0, archived: 0 };
  const hasActiveFilters = Boolean(searchInput) || selectedStatus !== null;

  // Filter-switch / pagination transition — identical motion to the courses
  // grid: one quick container fade, no exit-wait, no per-card stagger.
  const gridContainer = {
    hidden: reduceMotion ? { opacity: 1 } : { opacity: 0, y: 6 },
    visible: {
      opacity: 1,
      y: 0,
      transition: reduceMotion ? { duration: 0 } : { duration: 0.18, ease: EASE_OUT },
    },
  };
  const gridItem = { hidden: {}, visible: {} };
  const gridKey = programs.length ? programs.map((p) => p.id).join(',') : 'empty';

  // Free's allowance is 0 active programs, so the whole surface is a dead end
  // there — staff get the offer instead of a section they can never finish.
  if (!programsAllowed) {
    return (
      <motion.div {...slideUp}>
        <PageHeader title="Learning Programs" className="mb-6" />
        <FeatureUpgradeNotice
          feature="Learning Programs"
          description="Bundle courses into a multi-day journey, with its own audience, schedule and certificate."
          currentPlan={plan}
          icon={<AcademicCapIcon className="w-8 h-8" />}
        />
      </motion.div>
    );
  }

  return (
    <motion.div {...slideUp}>
      <PageHeader
        title="Learning Programs"
        className="pb-4 mb-8"
        trail={selectedStatus ? [{ label: 'Learning Programs', to: '/programs' }] : undefined}
        leaf={
          selectedStatus
            ? (
                {
                  draft: 'Drafts',
                  active: 'Published',
                  published: 'Published',
                  archived: 'Hidden',
                  disabled: 'Hidden',
                } as Record<string, string>
              )[selectedStatus]
            : undefined
        }
        actions={
          <button
            onClick={handleCreate}
            className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-bold bg-primary-500 rounded-full hover:bg-primary-600 dark:hover:bg-primary-400 transition-colors active:scale-95 shrink-0"
            data-testid="create-program-btn"
          >
            <PlusIcon className="w-5 h-5" />
            <span className="hidden sm:inline">Create Program</span>
          </button>
        }
      />

      {/* Search + Filter bar */}
      <div className="flex flex-col sm:flex-row items-center gap-3 mb-10">
        <img
          src={terySearchImg}
          alt=""
          className="w-[60px] h-[60px] object-contain -scale-x-100 pointer-events-none select-none shrink-0 hidden sm:block"
        />
        <input
          type="text"
          placeholder="Search programs by name..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          className="flex-1 h-11 px-4 text-sm bg-white dark:bg-grey-900 border border-border-primary/20 rounded-xl outline-none focus:border-primary-500 text-text-primary placeholder:text-text-tertiary transition-colors"
          data-testid="programs-search-input"
        />
      </div>

      {/* Error */}
      {error && (
        <div
          className="p-5 rounded-2xl bg-error-50 border border-error-200 mb-8"
          data-testid="programs-error"
        >
          <p className="text-sm text-error-700">
            Failed to load programs. Please try again later.
          </p>
        </div>
      )}

      <phantom-ui
        {...phantomProps(theme === 'dark', isLoading)}
        data-testid={isLoading ? 'programs-loading' : undefined}
      >
        {/* Loading placeholder cards */}
        {isLoading && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-7">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="h-[468px] rounded-[28px]" />
            ))}
          </div>
        )}

        {/* Content — the status tiles always render so a trainer can always
            click into Drafts / Published / Hidden; grid or empty state below. */}
        {!isLoading && !error && (
          <div className="flex flex-col gap-10">
            <section className="grid grid-cols-1 md:grid-cols-3 gap-5">
              <KpiTile
                title={`${draftCount} ${draftCount === 1 ? 'Draft' : 'Drafts'}`}
                description="Programs in progress"
                selected={selectedStatus === 'draft'}
                ariaLabel="Filter by drafts"
                onClick={() => selectStatus('draft')}
                iconComponent={{
                  icon: <CourseIcon />,
                }}
              />

              <KpiTile
                title={`${activeCount} Published`}
                description="Live for learners"
                selected={selectedStatus === 'active'}
                ariaLabel="Filter by active"
                onClick={() => selectStatus('active')}
                iconComponent={{
                  icon: <VerifiedIcon />,
                }}
              />

              <KpiTile
                title={`${archivedCount} Hidden`}
                description="Hidden from Learners"
                selected={selectedStatus === 'archived'}
                ariaLabel="Filter by hidden"
                onClick={() => selectStatus('archived')}
                iconComponent={{
                  icon: <HiddenIcon color={'rgb(var(--color-primary-400))'} accentColor={'rgb(var(--color-primary-100))'} />,
                }}
              />
            </section>

            {/* Revalidation cue while refetching with previous data on screen */}
            {isFetching && !isLoading && (
              <div
                data-testid="programs-refetching"
                aria-live="polite"
                className="flex items-center gap-2 -mt-2 text-sm font-semibold text-text-secondary"
              >
                <span className="h-3.5 w-3.5 rounded-full border-2 border-primary-500 border-t-transparent animate-spin" />
                Updating…
              </div>
            )}

            {programs.length === 0 ? (
              <div
                className="flex flex-col items-center justify-center py-16"
                data-testid="programs-empty-state"
              >
                <div className="w-20 h-20 rounded-full bg-primary-50 dark:bg-primary-900/20 flex items-center justify-center mb-5">
                  <CourseLibraryIcon className="w-10 h-10 text-primary-500" />
                </div>
                {hasActiveFilters ? (
                  <>
                    <h2 className="text-xl font-bold text-text-primary mb-2">
                      No programs match your filters
                    </h2>
                    <p className="text-sm text-text-secondary mb-6 text-center max-w-sm">
                      Try a different search or status.
                    </p>
                    <button
                      onClick={clearFilters}
                      className="text-sm font-bold text-primary-600 hover:text-primary-700 dark:text-primary-350 dark:hover:text-primary-300 transition-colors active:scale-95"
                    >
                      Clear filters
                    </button>
                  </>
                ) : (
                  <>
                    <h2 className="text-xl font-bold text-text-primary mb-2">No Programs Yet</h2>
                    <p className="text-sm text-text-secondary mb-6 text-center max-w-sm">
                      Get started by creating your first learning program.
                    </p>
                    <button
                      onClick={handleCreate}
                      className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-bold bg-primary-500 rounded-full hover:bg-primary-600 dark:hover:bg-primary-400 transition-colors"
                    >
                      <PlusIcon className="w-5 h-5" />
                      Create Program
                    </button>
                  </>
                )}
              </div>
            ) : (
              <>
                <motion.div
                  key={gridKey}
                  className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
                  data-testid="programs-grid"
                  variants={gridContainer}
                  initial="hidden"
                  animate="visible"
                >
                  {programs.map((program) => (
                    <motion.div key={program.id} variants={gridItem}>
                      <ProgramCard
                        program={program}
                        onToggleHidden={handleToggleHidden}
                        onDelete={handleDelete}
                        data-testid={`program-card-${program.id}`}
                      />
                    </motion.div>
                  ))}
                </motion.div>

                {/* Pagination */}
                {data?.pagination && data.pagination.pages > 1 && (
                  <div
                    className="flex items-center justify-center gap-4 pt-4"
                    data-testid="programs-pagination"
                  >
                    <button
                      onClick={() => handlePageChange(pageParam - 1)}
                      disabled={pageParam <= 1}
                      className="px-4 py-2 text-sm font-semibold text-text-primary border border-border-primary/20 rounded-full hover:bg-grey-50 dark:hover:bg-grey-800 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                      data-testid="programs-prev-page"
                    >
                      Previous
                    </button>
                    <span className="text-sm text-text-secondary">
                      Page {pageParam} of {data.pagination.pages}
                    </span>
                    <button
                      onClick={() => handlePageChange(pageParam + 1)}
                      disabled={pageParam >= data.pagination.pages}
                      className="px-4 py-2 text-sm font-semibold text-text-primary border border-border-primary/20 rounded-xl hover:bg-grey-50 dark:hover:bg-grey-800 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                      data-testid="programs-next-page"
                    >
                      Next
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </phantom-ui>
    </motion.div>
  );
};

export default ProgramsListPage;
